// Utility to check if a user profile exists
import { getApiUrl } from './apiConfig';
import { supabase } from './supabase';

/**
 * Dashboard path for a role, or null for an unknown role
 * @param {string} role - The user's role (student, teacher, parent)
 * @returns {string|null}
 */
export const getDashboardPath = (role) => {
  switch ((role || '').toLowerCase()) {
    case 'student':
      return '/student';
    case 'teacher':
      return '/teacher';
    case 'parent':
      return '/parent';
    default:
      return null;
  }
};

/**
 * Check whether a user's profile exists for the given role
 * @param {string} userId - The user ID from Supabase
 * @param {string} role - The user's role (student, teacher, parent)
 * @param {string} email - Optional email for teacher profile fallback
 * @returns {Promise<'exists'|'missing'|'error'>} 'error' means the check itself
 *   failed (backend down, bad session, ...) so we can't tell either way
 */
export const getProfileStatus = async (userId, role, email = null) => {
  try {
    if (!userId) {
      console.error('❌ No user ID provided for profile check');
      return 'error';
    }

    console.log(`🔍 Checking ${role} profile for user ID:`, userId);

    let url;
    let headers = {
      'Content-Type': 'application/json',
    };

    // Add JWT token for parent profiles (they require auth)
    if (role.toLowerCase() === 'parent') {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        console.error('❌ No valid session found for parent profile check:', sessionError);
        return 'error';
      }
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    // Build URL based on role
    switch (role.toLowerCase()) {
      case 'student':
        url = getApiUrl(`student-profile?user_id=${userId}`);
        break;
      case 'teacher':
        url = getApiUrl(`teacher-profile?user_id=${userId}`);
        if (email) {
          url += `&email=${encodeURIComponent(email)}`;
        }
        break;
      case 'parent':
        url = getApiUrl(`parent-profiles?user_id=${userId}`);
        break;
      default:
        console.error(`❌ Unknown role: ${role}`);
        return 'error';
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: headers,
    });

    // Profile doesn't exist if status is 404
    if (response.status === 404) {
      console.log(`⚠️ ${role} profile not found for user:`, userId);
      return 'missing';
    }

    if (!response.ok) {
      console.error(`❌ Error checking ${role} profile:`, response.status, response.statusText);
      return 'error';
    }

    // The parent endpoint also answers 200 when only the signup record exists,
    // so make sure an actual parent profile was filled in
    if (role.toLowerCase() === 'parent') {
      const data = await response.json();
      const hasProfile = typeof data.hasProfile === 'boolean'
        ? data.hasProfile
        : Boolean(data.yourStudentName); // older backend without hasProfile
      if (!hasProfile) {
        console.log('⚠️ Parent signed up but has not filled in their profile:', userId);
        return 'missing';
      }
    }

    console.log(`✅ ${role} profile exists for user:`, userId);
    return 'exists';
  } catch (error) {
    console.error(`❌ Exception checking ${role} profile:`, error);
    return 'error';
  }
};

/**
 * Check if a user profile exists for the given role
 * @returns {Promise<boolean>} True only if the profile definitely exists
 */
export const checkProfileExists = async (userId, role, email = null) =>
  (await getProfileStatus(userId, role, email)) === 'exists';













